import Button from '@/components/UI/Button';
import TagsInput from '@/components/UI/TagsInput/TagsInput';
import { altIds } from '@/constants/utils';
import { preventNumberScroll } from '@/helpers.tsx';
import { type Animal } from '@/model/Animal.model';
import { ErrorMessage } from '@hookform/error-message';
import { type CellChange } from '@/support/hot.types';
import { type FC, useEffect } from 'react';
import { Controller, FormProvider, type SubmitHandler, useForm, useFormContext, useWatch } from 'react-hook-form';
import {
  type FormFields,
  GenerateType,
  nameAnimalsInSequence,
  repeatIdentifiersAcrossCages,
  TAG_FIELD_KEY,
} from './BulkGenerateForm.utils';

const BulkGenerateForm = ({
  data,
  setData,
  tagUnique = false,
}: {
  data: Array<Animal>;
  setData: (dataChanges: Array<CellChange>) => void;
  tagUnique?: boolean;
}) => {
  const methods = useForm<FormFields>({
    defaultValues: {
      name: 'name',
      type: GenerateType.REPEATING,
      repeating_ids: [],
      sequence_prefix: '',
      sequence_starting: '',
    },
  });

  const onSubmitForm: SubmitHandler<FormFields> = (fields) => {
    if (fields.type === GenerateType.REPEATING) {
      setData(repeatIdentifiersAcrossCages(data, fields.name, fields.repeating_ids));
    } else if (fields.type === GenerateType.SEQUENCE) {
      setData(nameAnimalsInSequence(data, fields.name, fields.sequence_prefix, fields.sequence_starting));
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <BulkGenerate altIdOptions={altIds} onSubmit={onSubmitForm} tagUnique={tagUnique} />
      </FormProvider>
    </>
  );
};

const BulkGenerate: FC<{
  altIdOptions: Array<{ title: string; key: string }>;
  onSubmit: SubmitHandler<FormFields>;
  tagUnique: boolean;
}> = ({ altIdOptions, onSubmit, tagUnique }) => {
  const { handleSubmit, register, control, setValue } = useFormContext<FormFields>();

  const type = useWatch({ control, name: 'type' });
  const field = useWatch({ control, name: 'name' });

  // A repeating pattern deliberately reuses the same tokens across cages, which
  // produces duplicate values. When tag uniqueness is enforced, that option is
  // invalid for the Tag field, so hide it and force the sequence generator.
  const hideRepeating = tagUnique && field === TAG_FIELD_KEY;

  useEffect(() => {
    if (hideRepeating && type === GenerateType.REPEATING) {
      setValue('type', GenerateType.SEQUENCE);
    }
  }, [hideRepeating, type, setValue]);

  return (
    <form className="br2 ba b--moon-gray flex flex-wrap mb3 overflow-hidden" onSubmit={handleSubmit(onSubmit)}>
      <div className="w-50 br b--moon-gray pa3 flex justify-between items-center">
        <label className="mb0">Bulk assign IDs for:</label>
        <div className="flex">
          <select {...register('name')} className="w4 mr3 mb0" data-testid="id-field-select">
            {altIdOptions.map(({ title, key }) => (
              <option value={key} key={key}>
                {title}
              </option>
            ))}
          </select>
          <select {...register('type')} className="w4 mb0" data-testid="idGeneratorButton">
            {!hideRepeating && <option value="repeating">Repeating</option>}
            <option value="sequence">Sequence</option>
          </select>
        </div>
      </div>
      <div className="w-50 bg-near-white pa3 flex justify-between items-center" data-testid="identifier-tag-input">
        {type === GenerateType.REPEATING && !hideRepeating ? <RepeatIdentifiers /> : <PrefixAndSequence />}
        <Button submit stateless className="blue pr0" testId="bulkAssignButton">
          Bulk assign
        </Button>
      </div>
    </form>
  );
};

export const RepeatIdentifiers = () => {
  const { control } = useFormContext<FormFields>();
  return (
    <Controller
      control={control}
      name="repeating_ids"
      defaultValue={[]}
      render={({ field }) => (
        <TagsInput
          className="w-100 mr3 mb0"
          value={field.value}
          onChange={field.onChange}
          maxTags={10}
          onlyUnique
          placeholder="Separate tags with a comma"
          testId="repeating-ids-tags"
        />
      )}
    />
  );
};

export const PrefixAndSequence = () => {
  const {
    register,
    formState: { errors },
  } = useFormContext<FormFields>();
  return (
    <div className="flex">
      <input
        type="text"
        {...register('sequence_prefix')}
        placeholder="ABC"
        className="w4 mr3"
        style={{ marginBottom: 0 }}
      />
      <input
        type="number"
        onWheel={preventNumberScroll}
        {...register('sequence_starting', {
          valueAsNumber: false,
          min: { value: 0, message: 'Number must be greater than or equal to 0' },
        })}
        step="1"
        placeholder="123"
        className="w4 mr3"
        style={{ marginBottom: 0 }}
      />
      <ErrorMessage
        errors={errors}
        name="sequence_starting"
        render={({ message }) => (
          <small data-testid="sequence_starting-error-message" className="red db pt2">
            {message}
          </small>
        )}
      />
    </div>
  );
};

export default BulkGenerateForm;
